import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Home,
  Building2,
  Package,
  Box,
  Sparkles,
  MapPin,
  Settings,
} from 'lucide-react';

const ServiceCard: React.FC<{
  title: string;
  items: string[];
  icon: React.ReactNode;
}> = ({ title, items, icon }) => {
  return (
    <Card className="h-full transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-2 hover:border-primary/50">
      <CardHeader>
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white shadow-lg mb-4">
          {icon}
        </div>
        <CardTitle className="text-2xl font-bold text-gray-900">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {items.map((item, index) => (
            <li key={index} className="flex items-start">
              <Check className="flex-shrink-0 h-6 w-6 text-yellow-500 mr-3 mt-0.5" />
              <span className="text-gray-600">{item}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const Services: React.FC = () => {
  const { t } = useLanguage();
  
  const serviceData = [
    {
      titleKey: 'serviceResidential',
      icon: <Home className="w-8 h-8" />,
      itemKeys: ['serviceResidential1', 'serviceResidential2', 'serviceResidential3', 'serviceResidential4', 'serviceResidential5'],
    },
    {
      titleKey: 'serviceOffice',
      icon: <Building2 className="w-8 h-8" />,
      itemKeys: ['serviceOffice1', 'serviceOffice2', 'serviceOffice3', 'serviceOffice4'],
    },
    {
      titleKey: 'serviceSpecial',
      icon: <Package className="w-8 h-8" />,
      itemKeys: ['serviceSpecial1', 'serviceSpecial2', 'serviceSpecial3', 'serviceSpecial4'],
    },
    {
      titleKey: 'servicePacking',
      icon: <Box className="w-8 h-8" />,
      itemKeys: ['servicePacking1', 'servicePacking2', 'servicePacking3', 'servicePacking4'],
    },
    {
      titleKey: 'serviceCleaning',
      icon: <Sparkles className="w-8 h-8" />,
      itemKeys: ['serviceCleaning1', 'serviceCleaning2', 'serviceCleaning3'],
    },
    {
      titleKey: 'serviceCustom',
      icon: <Settings className="w-8 h-8" />,
      itemKeys: ['serviceCustom1', 'serviceCustom2', 'serviceCustom3'],
    },
    {
      titleKey: 'serviceSydney',
      icon: <MapPin className="w-8 h-8" />,
      itemKeys: ['serviceSydney1', 'serviceSydney2', 'serviceSydney3'],
    },
  ];

  return (
    <section id="services" className="py-20 sm:py-24 bg-slate-100">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
            {t('servicesTitle')}
          </h2>
          <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-600">
            {t('servicesSubtitle')}
          </p>
          <div className="mt-6 mx-auto w-24 h-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
        </div>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {serviceData.map((service, index) => (
            <ServiceCard
              key={index}
              title={t(service.titleKey)}
              items={service.itemKeys.map(key => t(key))}
              icon={service.icon}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
